import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Quaternion, Vector3, Vector4 } from '@babylonjs/core/Maths/math.vector';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import type { Scene } from '@babylonjs/core/scene';

import { as2d } from '../core/canvas2d';

/**
 * Name plates for nodes and edges, readable from any direction.
 *
 * **Yaw-billboarded, not fully billboarded.** The plate turns about Y only to face the viewer.
 * A full billboard tilts as you move and reads as an overlay pinned to your face; turning only
 * about the vertical keeps it upright, so it reads as a sign standing in the room. It is legible
 * from any direction *around* a node — which is the requirement — and it does not do anything
 * strange when you look down on a diagram from above.
 *
 * **Fixed physical size, not constant angular size.** Same reasoning: scaling to hold a
 * constant size on screen makes it behave like a HUD. So it is sized to read at about four
 * metres and fades out beyond eight, and the fade is what stops fifty labelled nodes becoming
 * fifty competing labels.
 *
 * Nothing is auto-named, so most nodes have no label and therefore no plate at all. Cost stays
 * proportional to how much has actually been written down, which matters because texture upload
 * is the Quest budget.
 */

/** Sized to read at about four metres without dominating the shape it names. */
const PLATE_HEIGHT = 0.075;

/** Small and fixed. Texture upload is the cost on a mobile GPU, not texture count. */
const TEXTURE_HEIGHT = 64;
const FONT_PX = 40;

/** Padding inside the plate, in texture pixels. */
const PADDING_PX = 22;

/** Fully opaque up to here, gone by FADE_END. */
const FADE_START = 6;
const FADE_END = 9;

/** `Mesh.DOUBLESIDE`, inlined so this module needs no value import of Mesh. */
const DOUBLE_SIDED = 2;

const BACKGROUND = '#12151aE6';
const TEXT_COLOR = '#E7ECF3';

interface Plate {
  mesh: Mesh;
  texture: DynamicTexture;
  material: StandardMaterial;
  text: string;
}

// spec: SPEC-086
export class LabelRenderer {
  private readonly plates = new Map<string, Plate>();

  /** Where each plate should sit this frame, refilled by the owner before update(). */
  private readonly anchors = new Map<string, Vector3>();

  constructor(
    private readonly scene: Scene,
    private readonly parent: TransformNode,
  ) {}

  /**
   * Creates, repaints or removes the plate for a key.
   *
   * Repaints only when the text actually changed — a node moving must not redraw its texture
   * every frame, which would turn a name plate into a per-frame upload.
   */
  set(key: string, text: string | null | undefined): void {
    const trimmed = text?.trim() ?? '';

    if (trimmed.length === 0) {
      this.remove(key);
      return;
    }

    const existing = this.plates.get(key);

    if (existing && existing.text === trimmed) {
      return;
    }

    if (existing) {
      this.remove(key);
    }

    this.plates.set(key, this.build(key, trimmed));
  }

  remove(key: string): void {
    const plate = this.plates.get(key);

    if (!plate) {
      return;
    }

    plate.texture.dispose();
    plate.material.dispose();
    plate.mesh.dispose();
    this.plates.delete(key);
    this.anchors.delete(key);
  }

  /** Where this plate hangs. Called by the owner each frame for whatever is labelled. */
  anchor(key: string, position: Vector3): void {
    const existing = this.anchors.get(key);

    if (existing) {
      existing.copyFrom(position);
      return;
    }

    this.anchors.set(key, position.clone());
  }

  /**
   * Faces every plate at the camera and applies the distance fade.
   *
   * Per frame, unavoidably: a yaw-billboard has to follow the *camera*, not the thing it
   * labels. Edges get away with refreshing only when a node moves; labels cannot. Kept cheap by
   * iterating only what is labelled and skipping anything past the fade.
   */
  update(): void {
    const camera = this.scene.activeCamera;

    if (!camera || this.plates.size === 0) {
      return;
    }

    const eye = camera.globalPosition;

    for (const [key, plate] of this.plates) {
      const anchor = this.anchors.get(key);

      if (!anchor) {
        plate.mesh.setEnabled(false);
        continue;
      }

      const distance = Vector3.Distance(eye, anchor);

      if (distance > FADE_END) {
        plate.mesh.setEnabled(false);
        continue;
      }

      plate.mesh.setEnabled(true);
      plate.mesh.position.copyFrom(anchor);

      // Yaw only. Taking atan2 of the horizontal offset means the plate never pitches, however
      // far above or below it the viewer is.
      //
      // Negated, and that is the whole trick: CreatePlane's normals are (0, 0, -1), so a
      // plane's *front* faces -Z. Yawing +Z at the viewer therefore presents the back, and
      // with back faces drawn you read the texture through it — mirrored. Point -Z instead.
      const toEye = eye.subtract(anchor);
      (plate.mesh.rotationQuaternion ??= Quaternion.Identity()).copyFrom(
        Quaternion.FromEulerAngles(0, Math.atan2(-toEye.x, -toEye.z), 0),
      );

      plate.material.alpha =
        distance <= FADE_START ? 1 : 1 - (distance - FADE_START) / (FADE_END - FADE_START);
    }
  }

  /** True when this key currently has a plate, so callers can skip anchoring what has none. */
  has(key: string): boolean {
    return this.plates.has(key);
  }

  /** Height of a plate, so callers can hang it clear of whatever it names. */
  get plateHeight(): number {
    return PLATE_HEIGHT;
  }

  clear(): void {
    for (const key of [...this.plates.keys()]) {
      this.remove(key);
    }
  }

  dispose(): void {
    this.clear();
  }

  private build(key: string, text: string): Plate {
    // Measured before the real texture is sized, so the plate is exactly as wide as its text.
    // A fixed-width plate either truncates "Order Service" or leaves "DB" swimming in space.
    const probe = new DynamicTexture(`label-probe-${key}`, { width: 8, height: 8 }, this.scene, false);
    const probeContext = as2d(probe.getContext());
    probeContext.font = `600 ${FONT_PX}px system-ui, -apple-system, "Segoe UI", sans-serif`;
    const textWidth = Math.ceil(probeContext.measureText(text).width);
    probe.dispose();

    const textureWidth = Math.max(TEXTURE_HEIGHT, textWidth + PADDING_PX * 2);
    const aspect = textureWidth / TEXTURE_HEIGHT;

    const texture = new DynamicTexture(
      `label-${key}`,
      { width: textureWidth, height: TEXTURE_HEIGHT },
      this.scene,
      true,
    );

    const context = as2d(texture.getContext());
    context.fillStyle = BACKGROUND;
    context.fillRect(0, 0, textureWidth, TEXTURE_HEIGHT);
    context.fillStyle = TEXT_COLOR;
    context.font = `600 ${FONT_PX}px system-ui, -apple-system, "Segoe UI", sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, textureWidth / 2, TEXTURE_HEIGHT / 2 + 2);
    texture.update();

    // Rounded corners come from the texture's own alpha, and the plate's translucent backdrop
    // needs it too, so the diffuse texture supplies opacity rather than a second texture.
    texture.hasAlpha = true;

    const material = new StandardMaterial(`label-${key}`, this.scene);
    material.diffuseTexture = texture;
    material.useAlphaFromDiffuseTexture = true;
    material.specularColor = Color3.Black();

    // Unlit: a name has to read the same wherever the room's light comes from, and a plate that
    // dims as you walk round it defeats the point of it facing you.
    material.emissiveTexture = texture;
    material.disableLighting = true;

    // Left at the material default (true / enabled) — deliberately, and the opposite of what
    // this line used to do. It used to read `material.backFaceCulling = false;` on the theory
    // that a frame where the camera crosses the plane would otherwise blink the plate out of
    // existence — wrong, once the plate is DOUBLE_SIDED: culling only ever discards whichever
    // of the two coincident triangle sets is *not* facing the camera, so one of them is always
    // rasterized regardless of which side you are on. Disabling culling did not add coverage;
    // it made both sets rasterize at once. They are exactly coincident (DOUBLESIDE duplicates
    // the source positions verbatim — VertexData._ComputeSides), so every fragment is an exact
    // depth tie, and Babylon's WebGL engine runs with gl.depthFunc(gl.LEQUAL) as its standing
    // default (ThinEngine's context setup, not overridden anywhere a StandardMaterial's own
    // `depthFunction` — left at its 0/"use the engine default" here) — LEQUAL resolves a tie in
    // favour of whichever fragment is rasterized *second*, which for one mesh's one index
    // buffer is deterministically the back-facing set DOUBLESIDE appends after the front one.
    // The result: the back set's deliberately-flipped backUVs always won, on every frame, from
    // every angle, regardless of the yaw being correct and regardless of backUVs being present
    // and correct — both of which were, and neither of which mattered. Confirmed empirically
    // against a real WebGL2 engine (NullEngine — what every test in this repo runs against —
    // never rasterizes or depth-tests anything, so no test here could have caught this): a
    // red/blue-split plate read mirrored with this line in place, from every angle tried, and
    // read correctly with it removed, while staying visible from both sides either way.
    material.backFaceCulling = true;

    const mesh = CreatePlane(
      `label-${key}`,
      {
        width: PLATE_HEIGHT * aspect,
        height: PLATE_HEIGHT,

        // Double-sided with the back's u axis flipped, so the reverse face reads forwards too.
        // Belt and braces over pointing the front at the viewer: "readable from any angle" is
        // the requirement, and a sign error in the yaw must not be able to reintroduce
        // mirrored text — which is exactly what it did the first time.
        sideOrientation: DOUBLE_SIDED,
        backUVs: new Vector4(1, 0, 0, 1),
        frontUVs: new Vector4(0, 0, 1, 1),
      },
      this.scene,
    );

    mesh.material = material;
    mesh.parent = this.parent;
    mesh.rotationQuaternion = Quaternion.Identity();

    // Never pickable: a label must not intercept the aim meant for the node behind it, nor
    // count as something you can grab.
    mesh.isPickable = false;

    return { mesh, texture, material, text };
  }
}
